import PropTypes from 'prop-types';
import React from 'react';

import t from '../../../labels/labelling-utils';
import { IGenericObject } from '../../../typings/common-types';
import { isValidArray } from '../../../utils/array';
import { isPropTypeNullable } from '../../../utils/prop-types';
import { rankingLegendItems, getRankingLegendsByQualifications } from '../../../utils/ranking-legends';
import { getClassificationByUEFACompetitionQualification } from '../../../utils/competition-qualification';
import { slugifyCamelCase } from '../../../utils/slugify';
import ErrorComponent from '../../atoms/error/error';
import ResultsTitle from '../../atoms/results-title/results-title';
import Spacer from '../../atoms/spacer/spacer';
import Legend from '../../molecules/legend/legend';
import CompetitionRankingTable, {
  ICompetitionRankingRow,
  ICompetitionRankingTable,
  CompetitionHighlight,
} from '../../organisms/competition-ranking-table/competition-ranking-table';
import ErrorBoundary from '../error-boundary/error-boundary';
import './competition-ranking.scss';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import { RoundName, roundNameList } from '../../../typings/match';

export type RankingVariant = 'teamRankingVariant' | 'competitionRankingVariant';

export const ROUND_UNICO: RoundName = 'unico';
export interface ICompetitionRanking {
  rankingLists: Array<ICompetitionRankingList>;
  team?: Array<string>; // TEAMS to HIGHLIGHT (competitionRankingVariant)
  teamId?: string; // TEAM to HIGHLIGHT in all COMPETITIONS RANKINGS (teamRankingVariant)
  trackingDataList?: Array<IGenericObject>; // Passed from WebApp, e.g. ['tracking-serie-a', 'tracking-champions-league'] for each COMPETITION inside rankingLists[]
}

export interface ICompetitionRankingList {
  competitionId: number | string; // FIXME: BE QUERIES return BOTH!
  seasonId: number | string; // FIXME: BE QUERIES return BOTH!
  teams?: Array<ICompetitionRankingRow>;
  customCompetitionName?: string;
  roundName?: RoundName;
}

const TEAM_RANKING_VARIANT: RankingVariant = 'teamRankingVariant';
const COMPETITION_RANKING_VARIANT: RankingVariant = 'competitionRankingVariant';

const getErrorComponent = () => <ErrorComponent>{t('unavailable.data')}</ErrorComponent>;

const getQualificationRankingLegendArray = (teams: Array<ICompetitionRankingRow>): CompetitionHighlight[] => [
  ...new Set(teams.map((team) => team.qualification).filter((elm) => Boolean(elm))),
];

/**
 * Transform uefa qualification object to Design System format
 */
export const transformTeamsUefaQualification = (teams: ICompetitionRankingRow[]): ICompetitionRankingRow[] =>
  teams.map((team) => {
    return {
      ...team,
      qualification: team.qualification
        ? getClassificationByUEFACompetitionQualification(team.qualification)
        : undefined,
    };
  });

const isLinearCompetitionByRoundName = (roundName: RoundName | undefined): boolean => roundName === ROUND_UNICO;

const isValidList = (rankingLists: Array<ICompetitionRankingList>) => Boolean(rankingLists && rankingLists.length > 0);

const getTrackingData = (trackingDataList?: Array<IGenericObject>, index: number = 0): IGenericObject | undefined =>
  trackingDataList && isValidArray(trackingDataList) && trackingDataList[index] ? trackingDataList[index] : undefined;

const getCompetitionName = (
  ranking: ICompetitionRankingList,
  isCompetitionRankingVariant: boolean,
  isLinearCompetition: boolean,
  index: number
): React.ReactNode => {
  const { roundName, customCompetitionName } = ranking;

  if (isCompetitionRankingVariant) {
    return (
      !isLinearCompetition && (
        <React.Fragment>
          <ResultsTitle title={`${t('competition.ranking.template.round')} ${roundName}`} titleFontSize="14--16" />
          <Spacer size={20} />
        </React.Fragment>
      )
    );
  } else {
    return (
      <React.Fragment>
        <ResultsTitle selfUpdate={index === 0} title={customCompetitionName ?? ''} titleFontSize="14--16" />
        <Spacer size={20} />
        {!isLinearCompetition && roundName && (
          <React.Fragment>
            <ResultsTitle title={`${t('competition.ranking.template.round')} ${roundName}`} titleFontSize="14--16" />
            <Spacer size={20} />
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
};

const getLegendComponent = (teams: Array<ICompetitionRankingRow>) => {
  const uefaQualificationsArray = getQualificationRankingLegendArray(teams);
  const legendsArray = uefaQualificationsArray.map(getClassificationByUEFACompetitionQualification);
  const rankingLegends = getRankingLegendsByQualifications(legendsArray);
  return <Legend items={rankingLegends} dataType="qualification" />;
};

const classNames = (): IClassNames =>
  makeClassNames({
    topScorers: 'top-scorers',
  });

const CompetitionRanking: React.FC<ICompetitionRanking> = ({
  /* istanbul ignore next: default value */ rankingLists = [],
  team,
  teamId,
  trackingDataList,
}) => {
  const cn = classNames();
  const variant: RankingVariant = teamId ? TEAM_RANKING_VARIANT : COMPETITION_RANKING_VARIANT;
  const isCompetitionRankingVariant: boolean = variant === COMPETITION_RANKING_VARIANT;

  return (
    <div className={cn.topScorers} data-variant={slugifyCamelCase(variant)}>
      <ErrorBoundary hasError={!isValidList(rankingLists)} errorComponent={getErrorComponent()}>
        {isCompetitionRankingVariant && (
          <ResultsTitle
            title={t('competition.ranking.template.teams')}
            selfUpdate
            titleFontSize="16"
            titlefontWeight="500"
          />
        )}
        {rankingLists.map((ranking: ICompetitionRankingList, index: number) => {
          const { roundName, teams = [] } = ranking;
          const transformedTeams = transformTeamsUefaQualification(teams);
          const isLinearCompetition = isLinearCompetitionByRoundName(roundName);
          const competitionRankingTable: ICompetitionRankingTable = {
            className: 'competition-ranking',
            rows: transformedTeams,
            showLastMatchTrend: true,
            team,
            teamId,
            trackingData: getTrackingData(trackingDataList, index),
          };

          /* istanbul ignore next: optional chaining not supported */
          return ranking?.teams?.length ? (
            <React.Fragment key={`${ranking?.seasonId}-${ranking?.competitionId}`}>
              <Spacer size={30} />
              {getCompetitionName(ranking, isCompetitionRankingVariant, isLinearCompetition, index)}
              <CompetitionRankingTable {...competitionRankingTable} />
              {isLinearCompetition ? getLegendComponent(teams) : null}
              <Legend items={rankingLegendItems} dataType="legend" />
            </React.Fragment>
          ) : null;
        })}
      </ErrorBoundary>
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  CompetitionRanking.propTypes = {
    rankingLists: PropTypes.arrayOf(
      PropTypes.exact({
        competitionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        customCompetitionName: isPropTypeNullable(PropTypes.string),
        groups: isPropTypeNullable(PropTypes.array), // FIXME
        roundName: isPropTypeNullable(PropTypes.oneOf(roundNameList)),
        seasonId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        teams: isPropTypeNullable(PropTypes.array), // FIXME
      }).isRequired
    ).isRequired,
    team: PropTypes.arrayOf(PropTypes.string.isRequired),
    teamId: PropTypes.string,
    trackingDataList: PropTypes.arrayOf(PropTypes.object.isRequired),
  };
}

export default CompetitionRanking;
